import { useMemo, useRef, useState } from "react";

import { faArrowLeftLong } from "@fortawesome/pro-regular-svg-icons";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup.js";
import { isNil } from "lodash-es";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";

import { Button } from "src/base-components/Button";
import { ConfirmationModal } from "src/base-components/ConfirmationModal";
import { TitleEditor } from "src/base-components/TitleEditor";
import {
  EntityMapping,
  FeatureQueryCreate,
  FeatureQueryPut,
  FeatureQueryStatus,
  PreviewQueryRequestEnvironmentEnum,
  PreviewQueryResponse,
} from "src/clients/features-control";
import { Icon } from "src/design-system/Icon";
import { useModal } from "src/design-system/Modal";
import { Skeleton } from "src/design-system/Skeleton";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { FeatureCatalogueWrapper } from "src/featureCatalogue/FeatureCatalogue";
import { ViewByType } from "src/featureCatalogue/Features/ViewTypeDropdown";
import { ActiveQueryConfirmationModal } from "src/featureCatalogue/Queries/ActiveQueryConfirmationModal";
import { FeatureQueryForm } from "src/featureCatalogue/Queries/Create/FeatureQueryForm";
import { MIN_COLUMNS_FOR_NON_GLOBAL_FEATURES } from "src/featureCatalogue/Queries/Create/helpers";
import type { QueryFormValues } from "src/featureCatalogue/Queries/Create/schema";
import {
  ColumnExistsValidationContext,
  defaultFormValues,
  schema,
} from "src/featureCatalogue/Queries/Create/schema";
import {
  useCreateFeatureQuery,
  useFeatureQuery,
  useFeatureQueryPreview,
  useUpdateFeatureQuery,
} from "src/featureCatalogue/queries";
import {
  getQueryEditPermissions,
  mapFeatureQueryToQuery,
  Query,
} from "src/featureCatalogue/utils";
import { useCapabilities } from "src/hooks/useCapabilities";
import { PageHeader } from "src/layout/PageHeader";
import { DashboardPageParams, getUrlToFeaturesPage } from "src/router/urls";
import { errorMessage } from "src/utils/stringUtils";
import { useParamsDecode } from "src/utils/useParamsDecode";

const DEFAULT_TITLE = "Untitled Feature Query";

const isNullishValue = (value: unknown): boolean =>
  isNil(value) || value === 0 || value === "None" || value === "none";

const hasAllNullishValues = (
  data: Record<string, unknown>[] | undefined,
): boolean => {
  if (!data || data.length === 0) return true;
  return data.every((row) => Object.values(row).every(isNullishValue));
};

type ManagedQuery = Query | Omit<Query, "id"> | undefined;

const getInitialQueryValue = (query: ManagedQuery): ManagedQuery => {
  if (!query) return query;
  return {
    ...query,
    window_hop:
      query.window_hop && query.window_hop < 60 ? 60 : query.window_hop,
    columns: {
      ...query.columns,
      features: query.columns?.features?.map((feature) => ({
        ...feature,
        feature_default: feature.feature_default || "None",
      })),
    },
  };
};

export const ManageFeatureQueryPage: React.FC = () => {
  const pendingSubmitData = useRef<QueryFormValues | null>(null);
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  const navigate = useNavigate();
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const {
    isOpen: isEmptyDataModalOpen,
    openModal: openEmptyDataModal,
    closeModal: closeEmptyDataModal,
  } = useModal();
  const createFeatureQueryMutation = useCreateFeatureQuery({
    invalidateFeatures: true,
  });
  const updateFeatureQueryMutation = useUpdateFeatureQuery({
    invalidateFeatures: true,
  });
  const [searchParams] = useSearchParams();
  const queryId = searchParams.get("queryId");
  const mode = searchParams.get("mode");
  const view = (searchParams.get("view") ?? "Feature") as ViewByType;
  const isDuplicate = mode === "duplicate";

  const { data: featureQueryData, isLoading: isQueryLoading } = useFeatureQuery(
    queryId ?? "",
  );
  const query = useMemo(() => {
    return mapFeatureQueryToQuery(featureQueryData);
  }, [featureQueryData]);

  const duplicateQuery = useMemo(() => {
    const queryName = query?.query_name ?? DEFAULT_TITLE;
    if (!isDuplicate || !query) return undefined;
    const { id: _dropId, ...rest } = query;
    return {
      ...rest,
      query_name: `Copy of ${queryName}`.trim(),
      columns: {
        ...query.columns,
      },
    };
  }, [isDuplicate, query]);

  const currentQuery = isDuplicate ? duplicateQuery : query;

  const { featureQueriesCatalogue } = useCapabilities();

  const isEditing = !!queryId && !isDuplicate;
  const { readOnly: readonly, readOnlyReason } = isEditing
    ? getQueryEditPermissions(currentQuery, featureQueriesCatalogue)
    : { readOnly: false, readOnlyReason: "" };

  const featureQueryPreview = useFeatureQueryPreview();

  const initialValues = getInitialQueryValue(currentQuery) as QueryFormValues;

  const form = useForm<QueryFormValues>({
    mode: "onChange",
    reValidateMode: "onChange",
    values: initialValues,
    defaultValues: initialValues ?? {
      ...defaultFormValues,
    },
    disabled: readonly,
    resolver: async (...args) => {
      // Ignore context, we pass it in manually
      const [data, _, options] = args;

      const response: PreviewQueryResponse["data"] | undefined =
        featureQueryPreview.previewData?.data;
      const availableColumns = Object.keys(response?.at(0) ?? {});

      // Hidden orphans (features whose column_name isn't in the current
      // preview) are kept in form state so they can be restored if the
      // column is added back to SQL — but they must not block validation.
      // Strip them before handing data to yup so `columnExistsValidation`
      // only runs against features that are actually visible. They'll also
      // be stripped at submit time so they don't reach the backend.
      const dataForValidation =
        availableColumns.length > 0
          ? {
              ...data,
              columns: {
                ...data.columns,
                features: (data.columns.features ?? []).filter(
                  (feature: { column_name: string }) =>
                    availableColumns.includes(feature.column_name),
                ),
              },
            }
          : data;

      return yupResolver(schema)(
        dataForValidation,
        {
          availableColumns,
          values: dataForValidation,
        } satisfies ColumnExistsValidationContext,
        options,
      );
    },
  });

  const [currentFormQuery, currentWindowLength, features, entityName] =
    useWatch({
      control: form.control,
      name: [
        "query",
        "window_length",
        "columns.features",
        "columns.entities.0.entity_name",
      ],
    });

  const { dirtyFields, errors } = form.formState;

  const hasQueryFieldsChanged = !!(
    dirtyFields.query || dirtyFields.window_length
  );

  const isPreviewSettled =
    !featureQueryPreview.isIdle &&
    !featureQueryPreview.isPending &&
    !featureQueryPreview.error &&
    featureQueryPreview.variables?.queryString === currentFormQuery &&
    featureQueryPreview.variables?.windowLength === currentWindowLength;

  const isEditMode = currentQuery && "id" in currentQuery;
  const previewColumns = Object.keys(
    featureQueryPreview.previewData?.data?.at(0) ?? {},
  );
  // Mirror the orphan-filter applied in the resolver and at submit time so
  // the button reflects what will actually be sent: a feature whose column
  // is no longer in the preview is an orphan and can't be submitted.
  const visibleFeatures =
    previewColumns.length > 0
      ? (features ?? []).filter((feature) =>
          previewColumns.includes(feature.column_name),
        )
      : (features ?? []);
  const hasFeatures = visibleFeatures.length > 0;
  const hasFormErrors = Object.keys(errors).length > 0;
  const hasDirtyFields = Object.keys(dirtyFields).length > 0;
  const previewHasRows =
    (featureQueryPreview.previewData?.data?.length ?? 0) > 0;
  const previewHasAllNullish =
    isPreviewSettled &&
    hasAllNullishValues(featureQueryPreview.previewData?.data);
  const previewColumnCount = previewColumns.length;
  const hasSingleColumnWithEntity =
    isPreviewSettled &&
    previewHasRows &&
    !!entityName &&
    previewColumnCount < MIN_COLUMNS_FOR_NON_GLOBAL_FEATURES;
  const canSubmit =
    !hasFormErrors &&
    hasFeatures &&
    !hasSingleColumnWithEntity &&
    (isEditMode
      ? hasDirtyFields && (!hasQueryFieldsChanged || isPreviewSettled)
      : isPreviewSettled);

  const isLiveActive =
    isEditing &&
    query?.environment_settings[PreviewQueryRequestEnvironmentEnum.LIVE]
      ?.status === FeatureQueryStatus.ACTIVE;

  const submitQuery = async (data: QueryFormValues) => {
    try {
      const entity = data.columns.entities?.[0];
      const hasEntity = !!entity?.column_name && !!entity?.entity_name;
      // Strip "orphan" features whose column_name is not in the latest
      // preview. Orphans are kept in form state during editing so they can
      // come back if the column is restored, but they must never reach the
      // backend — the column they reference no longer exists in the query.
      const previewColumns = new Set(
        Object.keys(featureQueryPreview.previewData?.data?.[0] ?? {}),
      );
      const allFeatures = data.columns.features ?? [];
      const featuresInPreview =
        previewColumns.size > 0
          ? allFeatures.filter((feature: { column_name: string }) =>
              previewColumns.has(feature.column_name),
            )
          : allFeatures;
      const payload: FeatureQueryCreate & FeatureQueryPut = {
        query: data.query,
        query_name: data.query_name,
        columns: {
          ...data.columns,
          entities: hasEntity ? [entity as EntityMapping] : [],
          features: featuresInPreview,
        },
        window_length: data.window_length,
        window_hop: data.window_hop,
      };

      if (isEditing && query?.id) {
        await updateFeatureQueryMutation.mutateAsync({
          ...payload,
          id: query.id,
          etag: query.etag,
        });
        toastActions.success({
          title: "Feature query updated",
          description: "Your feature query has been updated successfully",
        });
      } else {
        await createFeatureQueryMutation.mutateAsync(payload);
        toastActions.success({
          title: "Feature query created",
          description: "Your feature query has been created successfully",
        });
      }
      navigate(getUrlToFeaturesPage(orgId, wsId, view));
    } catch (error) {
      const message =
        errorMessage(error) ??
        `An unexpected error occurred while ${
          isEditing ? "updating" : "creating"
        } the feature query`;
      toastActions.failure({
        title: isEditing
          ? "Error updating feature query"
          : "Error creating feature query",
        description: message,
      });
    }
  };

  const handleSubmit = form.handleSubmit((data) => {
    if (previewHasAllNullish) {
      pendingSubmitData.current = data;
      openEmptyDataModal();
      return;
    }
    if (isLiveActive) {
      pendingSubmitData.current = data;
      setIsConfirmModalOpen(true);
    } else {
      return submitQuery(data);
    }
  });

  const isMutationPending =
    createFeatureQueryMutation.isPending ||
    updateFeatureQueryMutation.isPending;

  const isDeployDisabled = !canSubmit;

  const deployDisabledReason = (() => {
    if (readonly) return readOnlyReason;
    if (!isPreviewSettled) return "Run a preview before deploying";
    if (hasSingleColumnWithEntity)
      return "Feature Queries with target entities need at least two columns.";
    if (hasFormErrors) return "Complete all required fields to deploy";
    if (!hasFeatures) return "Add at least one feature before deploying";
    if (isEditMode && !hasDirtyFields) return "Make a change before deploying";
  })();

  const isLoading = (isEditing || isDuplicate) && isQueryLoading;

  return (
    <form className="h-full" onSubmit={handleSubmit}>
      <FormProvider {...form}>
        <ConfirmationModal
          confirmationButtonText="Deploy"
          open={isEmptyDataModalOpen}
          title="No data returned by Feature Query"
          onClose={closeEmptyDataModal}
          onConfirm={() => {
            closeEmptyDataModal();
            if (pendingSubmitData.current) {
              if (isLiveActive) {
                setIsConfirmModalOpen(true);
              } else {
                submitQuery(pendingSubmitData.current);
                pendingSubmitData.current = null;
              }
            }
          }}
        >
          <p className="font-inter-normal-12px text-gray-500">
            To preview results, adjust the End date and time to include sample
            data. Otherwise, deploy your changes to proceed without a preview.
          </p>
        </ConfirmationModal>
        {isLiveActive && query && isConfirmModalOpen && (
          <ActiveQueryConfirmationModal
            confirmationButtonText="Deploy"
            description="This Query is currently active on Live data. Deploying changes might break the Decision Flows and Jobs that use it."
            environment={PreviewQueryRequestEnvironmentEnum.LIVE}
            isOpen={isConfirmModalOpen}
            query={query}
            title="Deploy changes to Active Query?"
            onClose={() => setIsConfirmModalOpen(false)}
            onConfirm={() => {
              setIsConfirmModalOpen(false);
              if (pendingSubmitData.current) {
                submitQuery(pendingSubmitData.current);
                pendingSubmitData.current = null;
              }
            }}
          />
        )}
        <FeatureCatalogueWrapper
          breadcrumbs={[
            {
              key: "back-to-queries",
              breadcrumb: (
                <PageHeader.Breadcrumb hideIcon>
                  <NavLink
                    data-loc="back-to-queries"
                    to={getUrlToFeaturesPage(orgId, wsId, view)}
                  >
                    <Icon icon={faArrowLeftLong} size="xs" />
                  </NavLink>
                  {isLoading ? (
                    <Skeleton height="h-5" width="w-50" />
                  ) : (
                    <Controller
                      control={form.control}
                      name="query_name"
                      render={({ field }) => (
                        <TitleEditor
                          className="font-inter-semibold-13px"
                          dataLoc="title-editor"
                          disabled={readonly}
                          value={field.value || DEFAULT_TITLE}
                          onSubmit={field.onChange}
                          autofocus={!isEditing}
                        />
                      )}
                    />
                  )}
                </PageHeader.Breadcrumb>
              ),
            },
          ]}
          headerSideContent={
            <div className="flex gap-x-3 pr-4">
              <Tooltip
                disabled={canSubmit}
                title={deployDisabledReason}
                asChild
                placement="top"
              >
                <span>
                  <Button
                    dataLoc="deploy-button"
                    disabled={isDeployDisabled || readonly}
                    htmlType="submit"
                    loading={isMutationPending}
                  >
                    Deploy
                  </Button>
                </span>
              </Tooltip>
            </div>
          }
          showSubHeader={false}
        >
          <div className="h-full rounded-lg border border-gray-200 bg-white">
            <FeatureQueryForm
              featureQueryPreview={featureQueryPreview}
              form={form}
              hasFeatures={hasFeatures}
              isEditMode={!!isEditMode}
              isLoading={isLoading}
              readOnly={readonly}
            />
          </div>
        </FeatureCatalogueWrapper>
      </FormProvider>
    </form>
  );
};
